import React from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { withStyles } from "@mui/styles";
import spinner from "../../assign-prescriptions/images/spinner.gif";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import DeleteIcon from "@mui/icons-material/Delete";
import AddUserIcon from "@mui/icons-material/Add";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import Avatar from "@mui/material/Avatar";
import AvatarGroup from "@mui/material/AvatarGroup";
import { tooltipClasses } from "@mui/material/Tooltip";
import { withTranslation } from "react-i18next";
import { PageToolbarRow } from "../../common/PageToolbarRow";
import ILocalAGGrid, {
  getDefaultColDef,
  createServerSideDatasource,
  foldFiltersToSearchText,
  getActionsColumn,
  actionMenuItem,
  openAgGridContextMenu,
} from "../../grid/iLocalAGGrid";

const { log } = console;

const YesNoChip = (params) => {
  const raw =
    params.colDef && params.colDef.field && params.data
      ? params.data[params.colDef.field]
      : params.value;
  const yes =
    raw === true ||
    raw === 1 ||
    raw === "YES" ||
    raw === "yes" ||
    raw === "Y";
  return (
    <Chip
      label={yes ? "YES" : "NO"}
      color={yes ? "success" : "primary"}
      variant="outlined"
      size="small"
      sx={{
        "&.MuiChip-root": {
          fontSize: "0.6925rem",
          color: yes ? "#2E7D32" : "#FF0000",
          border: yes ? "1px solid #2E7D32" : "1px solid #FF0000",
        },
      }}
    />
  );
};

const toolbarStyles = (theme) => ({
  root: {
    paddingRight: theme.spacing.unit,
  },
  highlight:
    theme.palette.mode === "light"
      ? {
        color: "#4fc3f7",
        backgroundColor: "rgb(255, 234, 231)",
      }
      : {
        color: "#4fc3f7",
        backgroundColor: "rgb(255, 234, 231)",
      },
  spacer: {
    flex: "1 1 100%",
  },
  actions: {
    color: theme.palette.text.secondary,
  },
  title: {
    flex: "0 0 auto",
  },
  add: {
    backgroundColor: "#00a7e1",
    color: "#fff",
    transition: "background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
    "&:hover": {
      backgroundColor: "#6cca98",
      color: "#fff",
    },
  },
});

let EnhancedTableToolbar = (props) => {
  const {
    TotalNumberSelected,
    classes,
    AddAutomation,
    ConfirmDeleteAutomation,
    AddAutomationFlag,
    DeleteAutomationFlag,
    t,
  } = props;

  return (
    <PageToolbarRow highlight={TotalNumberSelected > 0}>
      {TotalNumberSelected > 0 && (
        <Typography variant="subheading">
          {TotalNumberSelected} {t("Automation.SelectedRows")}
        </Typography>
      )}
      <Box className={classes["actions"]}>
        {TotalNumberSelected === 1 || TotalNumberSelected > 1 ? (
          <Box>
            {DeleteAutomationFlag === true && (
              <Tooltip
                key="single-delete"
                title={t("Automation.DeleteTooltip")}
              >
                <IconButton
                  aria-label="Delete"
                  onClick={ConfirmDeleteAutomation}
                  sx={() => ({
                    "&:hover": { backgroundColor: "#6cca98", color: "#fff" },
                  })}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ) : AddAutomationFlag === true ? (
          <Tooltip key="single-add" title={t("Automation.AddTooltip")}>
            <IconButton
              aria-label="Add"
              className={classes["add"]}
              onClick={AddAutomation}
            >
              <AddUserIcon />
            </IconButton>
          </Tooltip>
        ) : (
          ""
        )}
      </Box>
    </PageToolbarRow>
  );
};

EnhancedTableToolbar.propTypes = {
  classes: PropTypes.object.isRequired,
  TotalNumberSelected: PropTypes.number.isRequired,
};
EnhancedTableToolbar = withStyles(toolbarStyles)(
  withTranslation()(EnhancedTableToolbar)
);

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: "10px 25px",
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const BootstrapDialogTitle = (props) => {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
};

BootstrapDialogTitle.propTypes = {
  children: PropTypes.node,
  onClose: PropTypes.func.isRequired,
};

const HTMLTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: "white",
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "white",
    color: "rgba(0, 0, 0, 0.87)",
    fontSize: theme.typography.pxToRem(12),
    border: "1px solid #dadde9",
    borderRadius: "5px",
    maxWidth: "fit-content",
  },
}));

const useStyles = (theme) => ({
  primaryButton: {
    background: "#6cca98",
    color: "#fff",
    "&:hover": {
      background: "#6cca98",
      color: "#fff",
    },
    "&:disabled": {
      color: "rgba(0, 0, 0, 0.38)",
      cursor: "not-allowed",
      background: "#E4E5E8",
    },
  },
  deleteButton: {
    "&:hover": {
      background: "#6cca98",
      color: "#fff",
    },
    "&:disabled": {
      color: "rgba(0, 0, 0, 0.38)",
      cursor: "not-allowed",
      background: "#E4E5E8",
    },
  },
  spinner: {
    position: "absolute",
    background: "rgba(255,255,255,.4)",
    width: "100%",
    height: "100%",
    zIndex: "9999",
    top: 0,
    left: 0,
  },
  spinnerImage: {
    top: "calc(50% - 30.5px)",
    left: "calc(50% - 65px)",
    position: "absolute",
    width: "130px",
    userSelect: "none",
  },
});

let filterObj = {
  searchtext: "",
  pageNo: 0,
  pageSize: 10,
  sortOrder: "desc",
  sortColumn: "created_on",
  tableName: "",
  searchObject: null,
};

class EmailAutomationComponent extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      distributionEmails: [],
      isPickupReport: false,
      isStockReport: false,
      isKioskUtilizationReport: false,
      isConsultReport: false,
      isTurnoverReport: false,
      isAvgtransactionReport: false,
      isSurveyReport: false,
      isIPayReceivablesReport: false,
      isIPayTransactionsReport: false,
      reminderOptionValue: "",
      weeklyReminderOptionValue: "",
      monthlyReminderDateValue: new Date(),
      Loader: false,
      automationState: [],
      selectedData: [],
      addAutomationFlag: true,
      deleteAutomationFlag: true,
      openDialogpopup: false,
      operation: "Add",
      data: [],
      openDeleteDialog: false,
      anchorEl: null,
      selectedRowID: null,
      confirmedDeleteList: [],
      innerLoader: false,
      totalRecords: 0,
      sortID: null,
      order: "",
      orderBy: "",
      helperText: "",
      selectedRow: {},
    };

    this.handleCheckboxChange = this.handleCheckboxChange.bind(this);
    this.handleOnChangeReminderOptions = this.handleOnChangeReminderOptions.bind(
      this
    );
    this.handleWeeklyDayChange = this.handleWeeklyDayChange.bind(this);
  }

  componentDidMount = async () => {
    // List data loads via ILocalAGGrid SSRM onGridReady
  };

  refreshGrid = () => {
    if (!this.gridApi) return;
    if (typeof this.gridApi.refreshServerSideStore === "function") {
      this.gridApi.refreshServerSideStore({ purge: true });
    } else if (typeof this.gridApi.refreshServerSide === "function") {
      this.gridApi.refreshServerSide({ purge: true });
    }
  };

  onGridReady = (params) => {
    this.gridApi = params.api;
    const datasource = createServerSideDatasource({
      filterBody: filterObj,
      applyFilterModel: (body, filterModel) => {
        body.searchtext = foldFiltersToSearchText(filterModel);
        body.searchObject =
          filterModel && Object.keys(filterModel).length ? filterModel : null;
      },
      pageZeroBased: true,
      getPageSize: () => filterObj.pageSize || 10,
      fetchPage: (body) =>
        fetch(process.env.REACT_APP_BASEURL + "automation/details", {
          method: "POST",
          withCredentials: true,
          headers: {
            Authorization:
              "Bearer " +
              JSON.parse(sessionStorage.getItem("user")).signInUserSession
                .idToken.jwtToken,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        })
          .then((r) => r.json())
          .then((response) => ({
            results:
              (response && response.results && response.results.results) || [],
            total: (response && response.results && response.results.total) || 0,
          })),
      onSuccess: (results, total) => {
        this.setState({
          data: results,
          totalRecords: total,
          Loader: false,
          isDataEmpty: !results || results.length === 0,
          selectedData: [],
        });
      },
      onError: () => this.setState({ Loader: false }),
    });
    if (typeof params.api.setServerSideDatasource === "function") {
      params.api.setServerSideDatasource(datasource);
    }
  };

  onRowSelected = (params) => {
    if (!params || !params.api) return;
    const selectedNodes = params.api.getSelectedNodes() || [];
    this.setState({
      selectedData: selectedNodes.map((n) => n.data && n.data.id).filter(Boolean),
    });
  };

  onPaginationChanged = (params) => {
    if (!params.api) return;
    const newPageSize = params.api.paginationGetPageSize();
    if (newPageSize && filterObj.pageSize !== newPageSize) {
      filterObj.pageSize = newPageSize;
    }
  };

  gettingAutomationDetailsFromDB = async () => {
    if (this.gridApi) {
      this.refreshGrid();
      return;
    }
    try {
      this.setState({ Loader: true });
      fetch(process.env.REACT_APP_BASEURL + "automation/details", {
        method: "POST",
        withCredentials: true,
        headers: {
          Authorization:
            "Bearer " +
            JSON.parse(sessionStorage.getItem("user")).signInUserSession.idToken
              .jwtToken,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(filterObj),
      })
        .then((response) => {
          try {
            return response.json();
          } catch (error) {
            return null;
          }
        })
        .then(async (response) => {
          this.setState({
            Loader: false,
            totalRecords:
              response && response["results"] && response["results"]["total"],
          });
          if (
            response &&
            Array.isArray(response["results"]["results"]) &&
            response["results"]["results"].length > 0
          ) {
            this.setState({
              data: response["results"]["results"],
              isDataEmpty: false,
            });
          } else this.setState({ data: [], isDataEmpty: true });
        });
    } catch (ErrorException) {
      this.setState({ Loader: false });
      log(
        "(EmailAutomationComponent): Error ocurred while getting the user automation details!",
        ErrorException
      );
    }
  };

  handleStates = () => {
    let selectedData =
      this.state.data.find((ele) => ele["id"] === this.state["selectedRowID"]) ||
      this.state.selectedRow;
    if (!selectedData) return;
    this.setState({
      distributionEmails: String(selectedData["distribution_emails"] || "").split(","),
      isPickupReport: selectedData["is_pickup_report"],
      isStockReport: selectedData["is_stock_report"],
      isKioskUtilizationReport: selectedData["is_kioskutil_report"],
      isConsultReport: selectedData["is_consult_report"],
      isTurnoverReport: selectedData["is_turnover_report"],
      isAvgtransactionReport: selectedData["is_avgtrans_report"],
      isSurveyReport: selectedData["is_survey_report"],
      isIPayReceivablesReport: selectedData["is_ipayreceivables_report"],
      isIPayTransactionsReport: selectedData["is_ipaytransactions_report"],
      reminderOptionValue: selectedData["reminder_option"],
      weeklyReminderOptionValue: selectedData["weekly_reminder_option"],
      monthlyReminderDateValue: selectedData["monthly_reminder_date"],
    });
  };

  handleOpenView = (event) => {
    this.setState({ operation: "View", openDialogpopup: true, isError: false });
    this.handleStates();
    this.handleContextMenuClose(event);
  };

  handleOpenEdit = (event) => {
    const selectedObject =
      this.state["data"].find(
        (ele) => ele["id"] === this.state["selectedRowID"]
      ) || this.state.selectedRow;
    this.setState({
      operation: "Edit",
      openDialogpopup: true,
      selectedRow: selectedObject,
      isError: false,
    });
    this.handleStates();
    this.handleContextMenuClose(event);
  };

  handleCheckboxChange = (event) => {
    const { name } = event.target;
    if (name === "PickupReport")
      this.setState({ isPickupReport: !this.state.isPickupReport });
    if (name === "StockReport")
      this.setState({ isStockReport: !this.state.isStockReport });
    if (name === "KioskUtilizationReport")
      this.setState({
        isKioskUtilizationReport: !this.state.isKioskUtilizationReport,
      });
    if (name === "ConsultReport")
      this.setState({ isConsultReport: !this.state.isConsultReport });
    if (name === "TurnoverReport")
      this.setState({ isTurnoverReport: !this.state.isTurnoverReport });
    if (name === "AvgtransactionReport")
      this.setState({
        isAvgtransactionReport: !this.state.isAvgtransactionReport,
      });
    if (name === "SurveyReport")
      this.setState({
        isSurveyReport: !this.state.isSurveyReport,
      });
    if (name === "iPayReceivablesReport")
        this.setState({ isIPayReceivablesReport: !this.state.isIPayReceivablesReport });
    if(name === "iPayTransactionsReport")
        this.setState({ isIPayTransactionsReport: !this.state.isIPayTransactionsReport });
  };

  handleOnChangeReminderOptions = (event) => {
    event && event.preventDefault();
    const { name } = event.target;
    this.setState(
      { reminderOptionValue: name, weeklyReminderOptionValue: "" },
      () => {
        if (name === "Monthly")
          this.setState({ monthlyReminderDateValue: new Date() });
      }
    );
  };

  handleWeeklyDayChange = (event) => {
    event && event.preventDefault();
    const { name } = event.target;
    this.setState({ weeklyReminderOptionValue: name });
  };

  handleMonthlyDateChange = (Date) =>
    this.setState({ monthlyReminderDateValue: Date });

  isEmail = (email) => {
    const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return pattern.test(email);
  };

  handleValidate = () => {
    //console.log('Length of distributionEmails: ' + this.state["distributionEmails"]);
    if (this.state["distributionEmails"]["length"] === 0) {
      this.setState({
        isError: true,
        helperText: this.props.t('Validation.EnterEmailAddress'),
      });
      return true;
    }

    let emails = [...new Set(this.state.distributionEmails)];
    let notValidEmailsList = emails.filter((ele) => !this.isEmail(ele));
    if (notValidEmailsList["length"] > 0) {
      this.setState({
        isError: true,
        helperText: "Please Enter Valid Email Address",
      });
      return true;
    }
  };

  prepareConfig = () => {
    let emailsLists = [...new Set(this.state["distributionEmails"])];
    let validEmailsList = emailsLists.filter((ele) => this.isEmail(ele));
    let config = {
      distributionEmails: [],
      pickupReportPremission: Boolean,
      stockReportPermission: Boolean,
      kioskUtilizationReportPremission: Boolean,
      consultReportPermisssion: Boolean,
      turnoverReportPermission: Boolean,
      avgTransactionReportPermission: Boolean,
      surveyReportPermission: Boolean,
      iPayReceivablesReportPermission: Boolean,
      iPayTransactionsReportPermission: Boolean,
      reminderOption: "",
      weeklyReminderOptionValue: "",
      monthlyReminderDateValue: "",
    };
    config["distributionEmails"] = validEmailsList;
    config["pickupReportPremission"] = this.state["isPickupReport"];
    config["stockReportPermission"] = this.state["isStockReport"];
    config["kioskUtilizationReportPremission"] = this.state[
      "isKioskUtilizationReport"
    ];
    config["consultReportPermisssion"] = this.state["isConsultReport"];
    config["turnoverReportPermission"] = this.state["isTurnoverReport"];
    config["avgTransactionReportPermission"] = this.state[
      "isAvgtransactionReport"
    ];
    config["surveyReportPermission"] = this.state["isSurveyReport"];
    config["iPayReceivablesReportPermission"] = this.state["isIPayReceivablesReport"];
    config["iPayTransactionsReportPermission"] = this.state["isIPayTransactionsReport"];
    config["reminderOption"] = this.state["reminderOptionValue"];
    config["weeklyReminderOptionValue"] = this.state[
      "weeklyReminderOptionValue"
    ];
    config["monthlyReminderDateValue"] = this.state["monthlyReminderDateValue"];
    return config;
  };

  handleSubmit = (event) => {
    event.preventDefault();
    console.log('Transactions report : ', this.state.isIPayTransactionsReport);
    try {
      if (this.handleValidate()) {
        return;
      }

      this.setState({ innerLoader: true });
      let config = this.prepareConfig();
      if (config["reminderOption"] === "") {
        config["reminderOption"] = "Daily";
        this.setState({ reminderOptionValue: "Daily" });
      }
      if (this.state["reminderOptionValue"] !== "Monthly") {
        config["monthlyReminderDateValue"] = null;
      }

      fetch(process.env.REACT_APP_BASEURL + "automation", {
        method: "POST",
        withCredentials: true,
        headers: {
          Authorization:
            "Bearer " +
            JSON.parse(sessionStorage.getItem("user")).signInUserSession.idToken
              .jwtToken,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(config),
      })
        .then((response) => response.json())
        .then(async (res) => {
          this.setState({
            Loader: false,
            innerLoader: false,
            openDialogpopup: false,
            distributionEmails: config["distributionEmails"],
          });
          let prepareStates = {
            distribution_emails: String(
              config["distributionEmails"]
            ).toString(),
            is_pickup_report: this.state["isPickupReport"],
            is_stock_report: this.state["isStockReport"],
            is_kioskutil_report: this.state["isKioskUtilizationReport"],
            is_consult_report: this.state["isConsultReport"],
            is_turnover_report: this.state["isTurnoverReport"],
            is_avgtrans_report: this.state["isAvgtransactionReport"],
            is_survey_report: this.state["isSurveyReport"],
            is_ipayreceivables_report: this.state["isIPayReceivablesReport"],
            is_ipaytransactions_report: this.state["isIPayTransactionsReport"],
            reminder_option: this.state["reminderOptionValue"],
            weekly_reminder_option: this.state["weeklyReminderOptionValue"],
            monthly_reminder_date: this.state["monthlyReminderDateValue"],
          };
          if (this.state["data"]["length"] === 0) {
            this.state["data"].push(prepareStates);
          } else this.state["data"].unshift(prepareStates);
          this.setState((state) => ({ data: state["data"] }));

          toast.success(this.props.t('Toast.Success.AutomationDetails'));
          await this.gettingAutomationDetailsFromDB();
        });
    } catch (SubmitException) {
      this.setState({
        Loader: false,
        innerLoader: false,
        openDialogpopup: false,
      });
      log(
        "(EmailAutomationComponent): Error ocurred while Submitting the user automation details!",
        SubmitException
      );
    }
  };

  handleUpdate = (event) => {
    event.preventDefault();
    try {
      if (this.handleValidate()) {
        return;
      }
      this.setState({ innerLoader: true });
      let config = this.prepareConfig();
      config["id"] = this.state["selectedRow"]["id"];
      if (config["reminderOption"] === "") {
        config["reminderOption"] = "Daily";
        this.setState({ reminderOptionValue: "Daily" });
      }
      if (this.state["reminderOptionValue"] !== "Monthly") {
        config["monthlyReminderDateValue"] = null;
      }

      fetch(process.env.REACT_APP_BASEURL + "automation", {
        method: "PUT",
        withCredentials: true,
        headers: {
          Authorization:
            "Bearer " +
            JSON.parse(sessionStorage.getItem("user")).signInUserSession.idToken
              .jwtToken,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(config),
      })
        .then((response) => response.json())
        .then(async (res) => {
          this.setState({
            Loader: false,
            innerLoader: false,
            openDialogpopup: false,
            distributionEmails: config["distributionEmails"],
          });
          let prepareStates = {
            distribution_emails: String(
              config["distributionEmails"]
            ).toString(),
            is_pickup_report: this.state["isPickupReport"],
            is_stock_report: this.state["isStockReport"],
            is_kioskutil_report: this.state["isKioskUtilizationReport"],
            is_consult_report: this.state["isConsultReport"],
            is_turnover_report: this.state["isTurnoverReport"],
            is_avgtrans_report: this.state["isAvgtransactionReport"],
            is_survey_report: this.state["isSurveyReport"],
            is_ipayreceivables_report: this.state["isIPayReceivablesReport"],
            is_ipaytransactions_report: this.state["isIPayTransactionsReport"],
            reminder_option: this.state["reminderOptionValue"],
            weekly_reminder_option: this.state["weeklyReminderOptionValue"],
            monthly_reminder_date: this.state["monthlyReminderDateValue"],
          };
          let data = this.state["data"].map((elem) => {
            if (elem["id"] === this.state["selectedRow"]["id"]) {
              return {
                ...prepareStates,
                id: elem["id"],
                parent_account_id: elem["parent_account_id"],
                user_id: elem["user_id"],
                discription: elem["discription"],
                created_on: elem["created_on"],
                created_by: elem["created_by"],
                is_deleted: elem["is_deleted"],
                is_enabled: elem["is_enabled"],
                modified_by: elem["modified_by"],
                modified_on: elem["modified_on"],
                deleted_on: elem["deleted_on"],
              };
            }
            return elem;
          });
          this.setState({ selectedRow: {}, data });
          await this.gettingAutomationDetailsFromDB();
        });
    } catch (UpdateException) {
      this.setState({
        Loader: false,
        innerLoader: false,
        openDialogpopup: false,
      });
      log(
        "(EmailAutomationComponent): Error ocurred while Updating the user automation details!",
        UpdateException
      );
    }
  };

  renderLoaderIndicator = () => {
    const { classes } = this.props;
    return (
      this.state.Loader && (
        <Box component="div" className={classes.spinner}>
          <Box
            component="img"
            className={classes.spinnerImage}
            src={spinner}
            alt="loading"
          />
        </Box>
      )
    );
  };

  renderinnerLoaderIndicator = () => {
    const { classes } = this.props;
    return (
      this.state.innerLoader && (
        <Box component="div" className={classes.spinner}>
          <Box
            component="img"
            className={classes.spinnerImage}
            src={spinner}
            alt="loading"
          />
        </Box>
      )
    );
  };

  handleOpenDialog = () => {
    this.setState({
      operation: "Add",
      openDialogpopup: true,
      distributionEmails: [],
      isPickupReport: false,
      isStockReport: false,
      isKioskUtilizationReport: false,
      isConsultReport: false,
      isTurnoverReport: false,
      isAvgtransactionReport: false,
      isSurveyReport: false,
      isIpayReceivablesReport: false,
      isIPayTransactionsReport: false,
      reminderOptionValue: "",
      weeklyReminderOptionValue: "",
      monthlyReminderDateValue: "",
      isError: false,  
    });
  };

  handleCloseDialog = () => {
    this.setState({ openDialogpopup: false, isError: false });
  };

  gettingOnSortTableName = (field) => {
    let tablename = "";
    if (
      [
        "Emails",
        "PickupReport",
        "StockReport",
        "KioskUtilReport",
        "ConsultReport",
        "AvgTurnoverReport",
        "AvgTransReport",
        "Reminder",
      ].includes(field)
    ) {
      tablename = "automation";
    }
    return tablename;
  };

  handleRequestSort = async (event, property, field) => {
    const orderBy = property;
    let order = "desc";
    if (this.state.order === "desc") order = "asc";

    this.setState({ order, orderBy, sortID: property });
    filterObj["sortOrder"] = order;
    filterObj["sortColumn"] = field;
    filterObj["tableName"] = this.gettingOnSortTableName(orderBy);
    const { data } = this.state;
    if (data && data["length"] > 0) {
      await this.gettingAutomationDetailsFromDB();
    }
  };

  handleChangePage = async (event, page) => {
    event && event.preventDefault();
    filterObj["pageNo"] = page;
    const { data } = this.state;
    if (data && data["length"] > 0) {
      await this.gettingAutomationDetailsFromDB();
    }
  };

  handleChangeRowsPerPage = async (event) => {
    const { data } = this.state;
    filterObj["pageNo"] = 0;
    filterObj["pageSize"] = event.target.value;
    if (data && data["length"] > 0) {
      await this.gettingAutomationDetailsFromDB();
    }
  };

  handleSelectAllCheckBoxClick = (event) => {
    if (event.target.checked) {
      this.setState((state) => ({ selectedData: state.data.map((n) => n.id) }));
      return;
    }
    this.setState({ selectedData: [] });
  };

  handleCheckBoxOrRowClick = (event, id) => {
    const { selectedData } = this.state;
    const selectedIndex = selectedData.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedData, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedData.slice(1));
    } else if (selectedIndex === selectedData.length - 1) {
      newSelected = newSelected.concat(selectedData.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedData.slice(0, selectedIndex),
        selectedData.slice(selectedIndex + 1)
      );
    }

    this.setState({ selectedData: newSelected });
  };

  isSelected = (id) => this.state.selectedData.indexOf(id) !== -1;

  handleContextMenuClick = (event, items) => {
    event.preventDefault();
    this.setState({
      anchorEl: event.currentTarget,
      selectedRowID: items["id"],
    });
  };

  handleContextMenuClose = (event) => {
    if (event && event.preventDefault) event.preventDefault();
    this.setState({ anchorEl: null });
  };

  openRowActionsMenu = (event, params, menuHelpers) => {
    const openMenu = () => {
      const items = this.getContextMenuItems({
        ...params,
        column: params.column,
        node: params.node,
      });
      if (menuHelpers && typeof menuHelpers.openMenu === "function") {
        menuHelpers.openMenu(items);
        return;
      }
      openAgGridContextMenu(
        params,
        (menuHelpers && menuHelpers.positionEvent) || event
      );
    };
    const row = params && params.data;
    this.setState(
      { selectedRowID: row && row.id, selectedRow: row || {} },
      openMenu
    );
  };

  getContextMenuItems = (params) => {
    const colId =
      params &&
      params.column &&
      (typeof params.column.getColId === "function"
        ? params.column.getColId()
        : params.column.colId);
    if (colId !== "actions") return [];
    const { t } = this.props;
    return [
      actionMenuItem({
        name: t("Automation.ViewActions"),
        icon: "view",
        action: () => this.handleOpenView({ preventDefault: () => {} }),
      }),
      actionMenuItem({
        name: t("Automation.EditActions"),
        icon: "edit",
        action: () => this.handleOpenEdit({ preventDefault: () => {} }),
      }),
      actionMenuItem({
        name: t("Automation.DeleteActions"),
        icon: "delete",
        action: () => this.handleOpenDeleteDialog(),
      }),
    ];
  };

  getColumnDefs = () => {
    const { t } = this.props;
    const yesNoCol = (field, headerName) => ({
      field,
      headerName,
      minWidth: 110,
      cellRenderer: YesNoChip,
      valueGetter: (params) => {
        const val = params.data && params.data[field];
        return val ? "YES" : "NO";
      },
    });
    return [
      {
        field: "distribution_emails",
        headerName: t("Automation.Email"),
        minWidth: 140,
        cellRenderer: (params) => {
          const emails =
            (params.data && params.data.distribution_emails) || "";
          return (
            <Stack direction="row" spacing={1}>
              <HTMLTooltip
                key="emails-tooltip"
                placement="right"
                title={
                  emails !== "" &&
                  emails.split(",").map((ele, idx) => (
                    <Chip
                      key={idx}
                      avatar={
                        <Avatar>
                          {String(ele).charAt(0).toUpperCase()}
                        </Avatar>
                      }
                      label={String(ele)}
                      sx={{
                        mb: "2%",
                        mt: idx === 0 && "2%",
                      }}
                    />
                  ))
                }
              >
                <AvatarGroup max={3}>
                  {emails !== "" &&
                    emails.split(",").map((ele, idx) => (
                      <Avatar key={idx}>
                        {String(ele).charAt(0).toUpperCase()}
                      </Avatar>
                    ))}
                </AvatarGroup>
              </HTMLTooltip>
            </Stack>
          );
        },
      },
      yesNoCol("is_pickup_report", t("Automation.PickupReport")),
      yesNoCol("is_stock_report", t("Automation.StockReport")),
      yesNoCol("is_kioskutil_report", t("Automation.KioskUtilReport")),
      yesNoCol("is_consult_report", t("Automation.ConsultReport")),
      yesNoCol("is_turnover_report", t("Automation.AvgTurnoverReport")),
      yesNoCol("is_avgtrans_report", t("Automation.AvgTransReport")),
      yesNoCol("is_survey_report", t("Automation.SurveyReport")),
      yesNoCol(
        "is_ipaytransactions_report",
        t("Automation.iPayTransactionsReport")
      ),
      yesNoCol(
        "is_ipayreceivables_report",
        t("Automation.iPayReceivablesReport")
      ),
      {
        field: "reminder_option",
        headerName: t("Automation.Reminder"),
        minWidth: 130,
        cellRenderer: (params) => {
          const option =
            (params.data && params.data.reminder_option) || "";
          const weekly =
            (params.data && params.data.weekly_reminder_option) || "";
          if (option === "Daily") {
            return (
              <Chip
                label={"Daily"}
                color="success"
                size="medium"
                sx={{
                  "&.MuiChip-root": {
                    fontSize: "0.7925rem",
                  },
                }}
              />
            );
          }
          if (option === "Weekly") {
            return (
              <Box component="span">
                Weekly
                <Chip
                  label={weekly}
                  color="success"
                  size="medium"
                  sx={{
                    "&.MuiChip-root": {
                      fontSize: "0.7925rem",
                      ml: "3%",
                    },
                  }}
                />
              </Box>
            );
          }
          if (option === "Monthly") {
            return (
              <Box component="span">
                <Chip
                  label={"Monthly"}
                  color="success"
                  size="medium"
                  sx={{
                    "&.MuiChip-root": {
                      fontSize: "0.7925rem",
                    },
                  }}
                />
              </Box>
            );
          }
          return null;
        },
      },
      getActionsColumn({
        headerName: t("Automation.Actions"),
        onActionsClick: (event, params, menuHelpers) =>
          this.openRowActionsMenu(event, params, menuHelpers),
      }),
    ];
  };

  handleOpenDeleteDialog = () => {
    if (this.state["selectedRowID"]) {
      let selectedRow = this.state.data
        .filter((x) => x["id"] === this.state["selectedRowID"])
        .map((elem) => elem["distribution_emails"]);
      if (
        (!selectedRow || !selectedRow.length) &&
        this.state.selectedRow &&
        this.state.selectedRow.distribution_emails
      ) {
        selectedRow = [this.state.selectedRow.distribution_emails];
      }

      this.setState({
        openDeleteDialog: true,
        confirmedDeleteList: String(selectedRow).split(","),
      });
    } else {
      let selectedRows = this.state.data
        .filter((x) => this.state["selectedData"].indexOf(x["id"]) > -1)
        .map((elem) => elem["distribution_emails"]);
      this.setState({
        openDeleteDialog: true,
        confirmedDeleteList: String(selectedRows).split(","),
      });
    }
  };

  handleCloseDeleteDialog = (event) => {
    if (this.state["selectedRowID"]) {
      this.setState({
        openDeleteDialog: false,
        selectedRowID: null,
        confirmedDeleteList: null,
      });
      this.handleContextMenuClose(event);
    } else
      this.setState({
        openDeleteDialog: false,
        selectedRowID: null,
        confirmedDeleteList: null,
      });
  };

  handleConfirmDelete = (event) => {
    if (this.state["selectedRowID"]) {
      this.handleConfirmDeleteRow(event, "Single");
    } else this.handleConfirmDeleteRow(event, "Multiple");
    this.setState({ openDeleteDialog: false });
  };

  handleConfirmDeleteRow = (event, ope) => {
    this.handleContextMenuClose(event);
    this.setState({ Loader: true });
    let URL =
      ope === "Single"
        ? `automation/${this.state["selectedRowID"]}`
        : "automation/" + this.state["selectedData"].join(",").trim(",");
    fetch(process.env.REACT_APP_BASEURL + URL, {
      method: "DELETE",
      withCredentials: true,
      headers: {
        "Content-Type": "application/json",
        Authorization:
          "Bearer " +
          JSON.parse(sessionStorage.getItem("user")).signInUserSession.idToken
            .jwtToken,
      },
    })
      .then((response) => {
        return response.json();
      })
      .then(async (result) => {
        if (result) {
          this.setState({
            selectedData: [],
            selectedRowID: null,
            Loader: false,
          });
          this.handleContextMenuClose(event);
          await this.gettingAutomationDetailsFromDB();
        }
      })
      .catch((error) => {
        this.setState({
          selectedData: [],
          selectedRowID: null,
          Loader: false,
        });
        log(
          "Error Ocurred while deleting the user automation details",
          JSON.stringify(error["message"])
        );
      });
  };

  render() {
    const { classes, t } = this.props;
    const {
      isPickupReport,
      isStockReport,
      isKioskUtilizationReport,
      isConsultReport,
      isTurnoverReport,
      isAvgtransactionReport,
      isSurveyReport,
      isIPayReceivablesReport,
      isIPayTransactionsReport,
      reminderOptionValue,
      weeklyReminderOptionValue,
      distributionEmails,
      selectedData,
      addAutomationFlag,
      deleteAutomationFlag,
      openDialogpopup,
      operation,
      isError,
      openDeleteDialog,
      confirmedDeleteList,
      helperText,
    } = this.state;
    const readOnly = operation === "View";
    return (
      <Box component="div">
        {this.renderLoaderIndicator()}
        <Box component="div" sx={{ width: "100%" }}>
          <BootstrapDialog
            maxWidth={"sm"}
            fullWidth={true}
            open={openDeleteDialog || false}
            onClose={this.handleCloseDeleteDialog}
            aria-labelledby="form-dialog-title"
            PaperProps={{
              sx: { maxHeight: "50%" },
            }}
          >
            {this.renderinnerLoaderIndicator()}
            <BootstrapDialogTitle
              id="customized-dialog-title"
              onClose={this.handleCloseDeleteDialog}
            >
              {t("Automation.AreYouSureToDeleteText")}
            </BootstrapDialogTitle>
            <DialogContent dividers>
              <Box component="div">
                {confirmedDeleteList &&
                  confirmedDeleteList.map((ele, index) => (
                    <Box
                      component="div"
                      key={index}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                      }}
                    >
                      <Chip
                        key={index}
                        avatar={
                          <Avatar>
                            {String(ele)
                              .charAt(0)
                              .toUpperCase()}
                          </Avatar>
                        }
                        label={String(ele)}
                        sx={{
                          mb: "2%",
                          mt: index === 0 && "2%",
                        }}
                      />
                    </Box>
                  ))}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={this.handleCloseDeleteDialog}
                color="primary"
                className={classes["primaryButton"]}
              >
                {t("Automation.Cancel")}
              </Button>
              <Button
                onClick={this.handleConfirmDelete}
                color="primary"
                autoFocus
                className={classes["primaryButton"]}
              >
                {t("Automation.Delete")}
              </Button>
            </DialogActions>
          </BootstrapDialog>
          <BootstrapDialog
            maxWidth={"md"}
            fullWidth={true}
            open={openDialogpopup || false}
            onClose={this.handleCloseDialog}
            aria-labelledby="form-dialog-title"
            PaperProps={{
              sx: { minWidth: "500px" },
            }}
          >
            {this.renderinnerLoaderIndicator()}
            <BootstrapDialogTitle
              id="customized-dialog-title"
              onClose={this.handleCloseDialog}
            >
              {t(operation)} {t("Automation.AutomationTitle")}
            </BootstrapDialogTitle>

            <DialogContent dividers>
              <Box component="div">
                <Grid container sx={{ mt: "1%" }}>
                  <Autocomplete
                    multiple
                    id="MultipleDistributeEmail"
                    name="MultipleDistributeEmail"
                    value={distributionEmails}
                    readOnly={readOnly}
                    limitTags={3}
                    options={[]}
                    freeSolo
                    onChange={(event, newValue, reason) => {
                      const newValues = newValue.map((ele) =>
                        String(ele).toLowerCase()
                      );
                      this.setState({
                        distributionEmails: newValues,
                        isError: false,
                      });
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        let isValid = this.isEmail(option);
                        return (
                          <Chip
                            variant="standard"
                            label={option.trim().toLowerCase()}
                            {...getTagProps({ index })}
                            sx={{
                              "&.MuiAutocomplete-tag": {
                                margin: "5px 5px 10px",
                              },
                              ".MuiChip-deleteIcon": {
                                color: !isValid ? "#FF0000" : "#6cca98",
                              },
                            }}
                          />
                        );
                      })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        type="text"
                        variant="standard"
                        autoCapitalize="characters"
                        label={t("Automation.Email")}
                        placeholder={t('Automation.EmailPlaceholder')}
                        error={isError}
                        helperText={isError && helperText}
                        sx={{
                          ".MuiFormHelperText-root": {
                            color: "red",
                          },
                        }}
                      />
                    )}
                    sx={{ width: "100%" }}
                  />
                </Grid>
                <Box component="div" sx={{ mt: "1%" }}>
                  <Typography
                    variant="h6"
                    component="h4"
                    sx={{ p: "10px 0px" }}
                  >
                    {" "}
                    {t("Automation.Permissions")}
                  </Typography>
                  <Grid container sx={{ justifyContent: "space-between" }}>
                    <Grid item lg={4}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            disabled={Boolean(readOnly)}
                            name="PickupReport"
                            checked={isPickupReport}
                            onChange={this.handleCheckboxChange}
                            value={isPickupReport}
                            color="primary"
                            inputProps={{
                              readOnly: Boolean(),
                            }}
                            sx={{
                              "&.Mui-checked": {
                                color: "#00a7e1 !important",
                              },
                            }}
                          />
                        }
                        label={
                          <Box
                            component="span"
                            style={{
                              color: "#000",
                              marginLeft: "0.5rem",
                            }}
                          >
                            {t("Automation.PickupReportToggle")}
                          </Box>
                        }
                        sx={{ ml: "0px" }}
                      />
                    </Grid>
                    <Grid item lg={4}>
                      <FormControlLabel
                        sx={{ ml: 0 }}
                        control={
                          <Checkbox
                            disabled={Boolean(readOnly)}
                            name="StockReport"
                            checked={isStockReport}
                            onChange={this.handleCheckboxChange}
                            value={isStockReport}
                            color="primary"
                            inputProps={{
                              readOnly: Boolean(),
                            }}
                            sx={{
                              "&.Mui-checked": {
                                color: "#00a7e1 !important",
                              },
                            }}
                          />
                        }
                        label={
                          <Box
                            component="span"
                            style={{
                              color: "#000",
                              marginLeft: "0.5rem",
                            }}
                          >
                            {t("Automation.StockReportToggle")}
                          </Box>
                        }
                      />
                    </Grid>
                    <Grid item lg={4}>
                      <FormControlLabel
                        sx={{ ml: 0 }}
                        control={
                          <Checkbox
                            disabled={Boolean(readOnly)}
                            name="KioskUtilizationReport"
                            checked={isKioskUtilizationReport}
                            onChange={this.handleCheckboxChange}
                            value={isKioskUtilizationReport}
                            color="primary"
                            inputProps={{
                              readOnly: Boolean(),
                            }}
                            sx={{
                              "&.Mui-checked": {
                                color: "#00a7e1 !important",
                              },
                            }}
                          />
                        }
                        label={
                          <Box
                            component="span"
                            style={{
                              color: "#000",
                              marginLeft: "0.5rem",
                            }}
                          >
                            {t("Automation.KioskUtilizationReportToggle")}
                          </Box>
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    sx={{ mt: "1%", justifyContent: "space-between" }}
                  >
                    <Grid item lg={4}>
                      <FormControlLabel
                        sx={{ ml: 0 }}
                        control={
                          <Checkbox
                            disabled={Boolean(readOnly)}
                            name="ConsultReport"
                            checked={isConsultReport}
                            onChange={this.handleCheckboxChange}
                            value={isConsultReport}
                            color="primary"
                            inputProps={{
                              readOnly: Boolean(),
                            }}
                            sx={{
                              "&.Mui-checked": {
                                color: "#00a7e1 !important",
                              },
                            }}
                          />
                        }
                        label={
                          <Box
                            component="span"
                            style={{
                              color: "#000",
                              marginLeft: "0.5rem",
                            }}
                          >
                            {t("Automation.ConsultReportToggle")}
                          </Box>
                        }
                      />
                    </Grid>
                    <Grid item lg={4}>
                      <FormControlLabel
                        sx={{ ml: 0 }}
                        control={
                          <Checkbox
                            disabled={Boolean(readOnly)}
                            name="TurnoverReport"
                            checked={isTurnoverReport}
                            onChange={this.handleCheckboxChange}
                            value={isTurnoverReport}
                            color="primary"
                            inputProps={{
                              readOnly: Boolean(),
                            }}
                            sx={{
                              "&.Mui-checked": {
                                color: "#00a7e1 !important",
                              },
                            }}
                          />
                        }
                        label={
                          <Box
                            component="span"
                            style={{
                              color: "#000",
                              marginLeft: "0.5rem",
                            }}
                          >
                            {t("Automation.TurnoverReportToggle")}
                          </Box>
                        }
                      />
                    </Grid>
                    <Grid item lg={4}>
                      <FormControlLabel
                        sx={{ ml: 0 }}
                        control={
                          <Checkbox
                            disabled={Boolean(readOnly)}
                            name="AvgtransactionReport"
                            checked={isAvgtransactionReport}
                            onChange={this.handleCheckboxChange}
                            value={isAvgtransactionReport}
                            color="primary"
                            inputProps={{
                              readOnly: Boolean(),
                            }}
                            sx={{
                              "&.Mui-checked": {
                                color: "#00a7e1 !important",
                              },
                            }}
                          />
                        }
                        label={
                          <Box
                            component="span"
                            style={{
                              color: "#000",
                              marginLeft: "0.5rem",
                            }}
                          >
                            {t("Automation.AvgTransactionReportToggle")}
                          </Box>
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    sx={{ mt: "1%", justifyContent: "flex-start" }}
                  >
                    <Grid item lg={4}>
                      <FormControlLabel
                        sx={{ ml: 0 }}
                        control={
                          <Checkbox
                            disabled={Boolean(readOnly)}
                            name="SurveyReport"
                            checked={isSurveyReport}
                            onChange={this.handleCheckboxChange}
                            value={isSurveyReport}
                            color="primary"
                            inputProps={{
                              readOnly: Boolean(),
                            }}
                            sx={{
                              "&.Mui-checked": {
                                color: "#00a7e1 !important",
                              },
                            }}
                          />
                        }
                        label={
                          <Box
                            component="span"
                            style={{
                              color: "#000",
                              marginLeft: "0.5rem",
                            }}
                          >
                            {t("Automation.SurveyReportToggle")}
                          </Box>
                        }
                      />
                    </Grid>

                    
                    <Grid item lg={4}> { /* iPayTransactions - mitesh vaghela 17/3/2025 */ }
                      <FormControlLabel
                        sx={{ ml: 0 }}
                        control={
                          <Checkbox
                            disabled={Boolean(readOnly)}
                            name="iPayTransactionsReport"
                            checked={isIPayTransactionsReport}
                            onChange={this.handleCheckboxChange}
                            value={isIPayTransactionsReport}
                            color="primary"
                            inputProps={{
                              readOnly: Boolean(),
                            }}
                            sx={{
                              "&.Mui-checked": {
                                color: "#00a7e1 !important",
                              },
                            }}
                          />
                        }
                        label={
                          <Box
                            component="span"
                            style={{
                              color: "#000",
                              marginLeft: "0.5rem",
                            }}
                          >
                            {t("Automation.iPayTransactionsReportToggle")}
                          </Box>
                        }
                      />
                    </Grid>


                    <Grid item lg={4}> { /* iPayReceivables - mitesh vaghela 24/2/2025 */ }
                      <FormControlLabel
                        sx={{ ml: 0 }}
                        control={
                          <Checkbox
                            disabled={Boolean(readOnly)}
                            name="iPayReceivablesReport"
                            checked={isIPayReceivablesReport}
                            onChange={this.handleCheckboxChange}
                            value={isIPayReceivablesReport}
                            color="primary"
                            inputProps={{
                              readOnly: Boolean(),
                            }}
                            sx={{
                              "&.Mui-checked": {
                                color: "#00a7e1 !important",
                              },
                            }}
                          />
                        }
                        label={
                          <Box
                            component="span"
                            style={{
                              color: "#000",
                              marginLeft: "0.5rem",
                            }}
                          >
                            {t("Automation.iPayReceivablesReportToggle")}
                          </Box>
                        }
                      />
                    </Grid> 
                  </Grid>
                  
                </Box>
                <Box component="div">
                  <Typography
                    variant="h6"
                    component="h4"
                    sx={{ p: "10px 0px" }}
                  >
                    {t("Automation.ReminderOptions")}
                  </Typography>
                  <Grid container>
                    <FormControl sx={{ width: "100%" }}>
                      <RadioGroup
                        aria-labelledby="radio-buttons-group-label"
                        name="row-radio-buttons-group"
                      >
                        <FormControlLabel
                          name="Daily"
                          value="Daily"
                          disabled={Boolean(readOnly)}
                          control={
                            <Radio
                              sx={{
                                "&.Mui-checked": {
                                  color: "#00a7e1 !important",
                                },
                              }}
                            />
                          }
                          label={t("Automation.Daily")}
                          checked={reminderOptionValue === "Daily"}
                          onChange={this.handleOnChangeReminderOptions}
                          sx={{
                            mb: "5px",
                            ml: "0px",
                          }}
                        />
                        <Box component="span">
                          <FormControlLabel
                            name="Weekly"
                            value="Weekly"
                            disabled={Boolean(readOnly)}
                            control={
                              <Radio
                                sx={{
                                  "&.Mui-checked": {
                                    color: "#00a7e1 !important",
                                  },
                                }}
                              />
                            }
                            label={t("Automation.Weekly")}
                            checked={reminderOptionValue === "Weekly"}
                            onChange={this.handleOnChangeReminderOptions}
                            sx={{
                              mb: "5px",
                              ml: "0px",
                            }}
                          />
                          {reminderOptionValue === "Weekly" && (
                            <Box
                              component="span"
                              sx={{ ml: "5px", fontSize: "12px" }}
                            >
                              <Box component="b" sx={{ fontWeight: "500" }}>
                                {t("Automation.WeeklyNote")}:
                              </Box>{" "}
                              {t("Automation.WeeklyNoteText")}
                            </Box>
                          )}
                        </Box>
                        {reminderOptionValue === "Weekly" && (
                          <Box sx={{ m: "5px 12px", mb: "5px" }}>
                            <FormControl sx={{ width: "100%" }}>
                              <RadioGroup
                                row
                                aria-labelledby="radio-buttons-group-label"
                                name="row-radio-buttons-group"
                                sx={{ justifyContent: "space-between" }}
                              >
                                <FormControlLabel
                                  name="Monday"
                                  value="Monday"
                                  disabled={Boolean(readOnly)}
                                  control={
                                    <Radio
                                      sx={{
                                        "&.Mui-checked": {
                                          color: "#00a7e1 !important",
                                        },
                                      }}
                                    />
                                  }
                                  label={t("Automation.Monday")}
                                  checked={
                                    weeklyReminderOptionValue === "Monday"
                                  }
                                  onChange={this.handleWeeklyDayChange}
                                  sx={{
                                    ml: "0px",
                                    mr: "0px",
                                  }}
                                />
                                <FormControlLabel
                                  name="Tuesday"
                                  value="Tuesday"
                                  disabled={Boolean(readOnly)}
                                  control={
                                    <Radio
                                      sx={{
                                        "&.Mui-checked": {
                                          color: "#00a7e1 !important",
                                        },
                                      }}
                                    />
                                  }
                                  label={t("Automation.Tuesday")}
                                  checked={
                                    weeklyReminderOptionValue === "Tuesday"
                                  }
                                  onChange={this.handleWeeklyDayChange}
                                  sx={{
                                    ml: "0px",
                                    mr: "0px",
                                  }}
                                />
                                <FormControlLabel
                                  name="Wednesday"
                                  value="Wednesday"
                                  disabled={Boolean(readOnly)}
                                  control={
                                    <Radio
                                      sx={{
                                        "&.Mui-checked": {
                                          color: "#00a7e1 !important",
                                        },
                                      }}
                                    />
                                  }
                                  label={t("Automation.Wednesday")}
                                  checked={
                                    weeklyReminderOptionValue === "Wednesday"
                                  }
                                  onChange={this.handleWeeklyDayChange}
                                  sx={{
                                    ml: "0px",
                                    mr: "0px",
                                  }}
                                />
                                <FormControlLabel
                                  name="Thursday"
                                  value="Thursday"
                                  disabled={Boolean(readOnly)}
                                  control={
                                    <Radio
                                      sx={{
                                        "&.Mui-checked": {
                                          color: "#00a7e1 !important",
                                        },
                                      }}
                                    />
                                  }
                                  label={t("Automation.Thursday")}
                                  checked={
                                    weeklyReminderOptionValue === "Thursday"
                                  }
                                  onChange={this.handleWeeklyDayChange}
                                  sx={{
                                    ml: "0px",
                                    mr: "0px",
                                  }}
                                />
                                <FormControlLabel
                                  name="Friday"
                                  value="Friday"
                                  disabled={Boolean(readOnly)}
                                  control={
                                    <Radio
                                      sx={{
                                        "&.Mui-checked": {
                                          color: "#00a7e1 !important",
                                        },
                                      }}
                                    />
                                  }
                                  label={t("Automation.Friday")}
                                  checked={
                                    weeklyReminderOptionValue === "Friday"
                                  }
                                  onChange={this.handleWeeklyDayChange}
                                  sx={{
                                    ml: "0px",
                                    mr: "0px",
                                  }}
                                />
                                <FormControlLabel
                                  name="Saturday"
                                  value="Saturday"
                                  disabled={Boolean(readOnly)}
                                  control={
                                    <Radio
                                      sx={{
                                        "&.Mui-checked": {
                                          color: "#00a7e1 !important",
                                        },
                                      }}
                                    />
                                  }
                                  label={t("Automation.Saturday")}
                                  checked={
                                    weeklyReminderOptionValue === "Saturday"
                                  }
                                  onChange={this.handleWeeklyDayChange}
                                  sx={{
                                    ml: "0px",
                                    mr: "0px",
                                  }}
                                />
                                <FormControlLabel
                                  name="Sunday"
                                  value="Sunday"
                                  disabled={Boolean(readOnly)}
                                  control={
                                    <Radio
                                      sx={{
                                        "&.Mui-checked": {
                                          color: "#00a7e1 !important",
                                        },
                                      }}
                                    />
                                  }
                                  label={t("Automation.Sunday")}
                                  checked={
                                    weeklyReminderOptionValue === "Sunday"
                                  }
                                  onChange={this.handleWeeklyDayChange}
                                  sx={{
                                    ml: "0px",
                                    mr: "0px",
                                  }}
                                />
                              </RadioGroup>
                            </FormControl>
                          </Box>
                        )}
                        <Box component="span">
                          <FormControlLabel
                            name="Monthly"
                            value="Monthly"
                            disabled={Boolean(readOnly)}
                            control={
                              <Radio
                                sx={{
                                  "&.Mui-checked": {
                                    color: "#00a7e1 !important",
                                  },
                                }}
                              />
                            }
                            label={t("Automation.Monthly")}
                            checked={reminderOptionValue === "Monthly"}
                            onChange={this.handleOnChangeReminderOptions}
                            sx={{ ml: "0px" }}
                          />
                          {reminderOptionValue === "Monthly" && (
                            <Box
                              component="span"
                              sx={{ ml: "5px", fontSize: "12px" }}
                            >
                              <Box component="b" sx={{ fontWeight: "500" }}>
                                {t("Automation.MonthlyNote")}:
                              </Box>{" "}
                              {t("Automation.MonthlyNoteText")}
                            </Box>
                          )}
                        </Box>
                        {/* {reminderOptionValue === "Monthly" && (
                          <Box sx={{ ml: "41px", mb: "10px" }}>
                            <FormControl sx={{ widht: "50%" }}>
                              <LocalizationProvider
                                dateAdapter={AdapterDateFns}
                              >
                                <DesktopDatePicker
                                  label="Date"
                                  views={["month"]}
                                  disabled={Boolean(readOnly)}
                                  value={monthlyReminderDateValue}
                                  onChange={this.handleMonthlyDateChange}
                                  renderInput={(params) => (
                                    <TextField
                                      variant="standard"
                                      autoFocus={false}
                                      {...params}
                                    />
                                  )}
                                />
                              </LocalizationProvider>
                            </FormControl>
                          </Box>
                        )} */}
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={this.handleCloseDialog}
                color="primary"
                className={classes["primaryButton"]}
              >
                {operation === "View"
                  ? t("Automation.Close")
                  : t("Automation.Cancel")}
              </Button>
              {operation !== "View" && (
                <Button
                  disabled={this.state.isButtonDisabled}
                  color="primary"
                  className={classes["primaryButton"]}
                  onClick={(event) => {
                    operation === "Add"
                      ? this.handleSubmit(event)
                      : this.handleUpdate(event);
                  }}
                >
                  {operation === "Add"
                    ? t("Automation.Save")
                    : t("Automation.Update")}
                </Button>
              )}
            </DialogActions>
          </BootstrapDialog>

          <EnhancedTableToolbar
            TotalNumberSelected={selectedData.length}
            AddAutomation={this.handleOpenDialog}
            ConfirmDeleteAutomation={this.handleOpenDeleteDialog}
            AddAutomationFlag={addAutomationFlag}
            DeleteAutomationFlag={deleteAutomationFlag}
          />
          <Divider />
          <Box component="div" sx={{ overflowX: 'auto' }}>
            <ILocalAGGrid
              columnDefs={this.getColumnDefs()}
              defaultColDef={getDefaultColDef({ minWidth: 100 })}
              rowSelection="multiple"
              rowModelType="serverSide"
              cacheBlockSize={filterObj.pageSize || 10}
              paginationPageSize={filterObj.pageSize || 10}
              paginationPageSizeSelector={[10, 20, 25]}
              rowHeight={50}
              getRowId={(params) => params.data && String(params.data.id)}
              onGridReady={this.onGridReady}
              onRowSelected={this.onRowSelected}
              onPaginationChanged={this.onPaginationChanged}
              getContextMenuItems={this.getContextMenuItems}
            />
          </Box>
        </Box>
      </Box>
    );
  }
}

export default withStyles(useStyles, { withTheme: true })(
  withTranslation()(EmailAutomationComponent)
);



// WEBPACK FOOTER //
// ./src/components/automation/email-automation/index.js